import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, Lock, UserRound, Menu, X, Download, Truck, ChevronDown, LayoutGrid } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";
import { api } from "@/lib/api";
import { downloadAsset } from "@/lib/download";
import { BRAND, isAdminPhone } from "@/config";
import { OfferStrip } from "@/components/store/OfferStrip";

const FALLBACK_LINKS = [
  { label: "Catalog", href: "#catalog" },
  { label: "New Launch", href: "#new-launch" },
  { label: "Manifesto", href: "#manifesto" },
  { label: "Heat Press Specs", href: "#heat-press" },
];

const MENU_HREFS = ["#catalog", "#manifesto", "#heat-press"];
const SECTION_HREFS = ["#top", "#catalog", "#manifesto", "#heat-press", "#new-launch"];

const testId = (label) => `nav-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

export default function Navbar() {
  const { count, setDrawerOpen } = useCart();
  const { customer, signOut } = useCustomer();
  const [links, setLinks] = useState(FALLBACK_LINKS);
  const [menuOpen, setMenuOpen] = useState(false);
  const [barOpen, setBarOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [staffPhone, setStaffPhone] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catsOpen, setCatsOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const showAdmin = isAdminPhone(customer?.phone) || staffPhone;
  const menuLinks = links.filter((l) => MENU_HREFS.includes(l.href));
  const inlineLinks = links.filter((l) => !MENU_HREFS.includes(l.href));

  const downloadCatalogue = async () => {
    if (downloading) return;
    setDownloading(true);
    await downloadAsset("/catalogue/download", "varsha-sublimation-catalogue.pdf", "catalogue");
    setDownloading(false);
  };

  useEffect(() => {
    api.get("/settings")
      .then(({ data }) => {
        const clean = (data.nav_links || []).filter((l) => l.label && l.href);
        if (clean.length) setLinks(clean);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const phone = customer?.phone;
    if (!phone || isAdminPhone(phone)) { setStaffPhone(false); return; }
    api.get(`/staff/allowed/${phone}`)
      .then(({ data }) => setStaffPhone(!!data.allowed))
      .catch(() => setStaffPhone(false));
  }, [customer?.phone]);

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/popular").catch(() => ({ data: { categories: [] } }))])
      .then(([cats, pop]) => {
        const rank = new Map((pop.data.categories || []).map((c, i) => [c.name, i]));
        const live = (cats.data || []).filter((c) => c.product_count > 0);
        live.sort((a, b) => {
          const ra = rank.has(a.name) ? rank.get(a.name) : Infinity;
          const rb = rank.has(b.name) ? rank.get(b.name) : Infinity;
          return ra === rb ? a.name.localeCompare(b.name) : ra - rb;
        });
        setCategories(live);
      })
      .catch(() => {});
  }, []);

  const openCategory = (name) => {
    setBarOpen(false);
    setCatsOpen(false);
    setMenuOpen(false);
    window.dispatchEvent(new CustomEvent("vs:nav", { detail: { label: name, href: "" } }));
  };

  const handleNavClick = (e, link) => {
    if (SECTION_HREFS.includes(link.href) || /^https?:/i.test(link.href)) return;
    e.preventDefault();
    window.dispatchEvent(new CustomEvent("vs:nav", { detail: { label: link.label, href: link.href } }));
  };

  useEffect(() => {
    const close = (e) => { if (!e.target.closest?.("[data-nav-menu]")) setBarOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <header
      data-testid="main-nav"
      className="sticky top-0 z-50 bg-[#0A0B0E]/85 backdrop-blur-xl border-b border-white/10"
    >
      <OfferStrip />
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4 lg:px-8 xl:max-w-[1500px]">
        <a href="#top" data-testid="nav-brand" className="flex shrink-0 items-center gap-2.5 group sm:gap-3">
          <span data-testid="nav-logo" className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-cyan-400 via-fuchsia-500 to-yellow-300 transition-transform duration-300 group-hover:scale-105">
            <span className="absolute inset-[1.5px] rounded-[10px] bg-[#0A0B0E]" />
            <span className="relative font-display text-sm font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-br from-cyan-300 via-fuchsia-400 to-yellow-300">
              VS
            </span>
          </span>
          <span className="whitespace-nowrap font-display text-sm font-extrabold tracking-tight sm:text-lg">{BRAND}</span>
        </a>

        <div className="hidden min-w-0 items-center gap-4 lg:flex">
          <div className="no-scrollbar flex min-w-0 items-center gap-4 overflow-x-auto" data-testid="nav-inline-links">
            {inlineLinks.map((l) => (
              <a
                key={l.href + l.label}
                href={l.href}
                data-testid={testId(l.label)}
                onClick={(e) => handleNavClick(e, l)}
                className={`whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.12em] transition-colors duration-300 xl:tracking-[0.15em] ${l.href === "#new-launch" ? "text-yellow-300 hover:text-yellow-200" : "text-slate-400 hover:text-cyan-300"}`}
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <button
            data-testid="track-order-btn"
            onClick={() => window.dispatchEvent(new CustomEvent("vs:my-orders"))}
            title="Track your order"
            className="hidden items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-slate-400 transition-colors hover:text-cyan-300 lg:flex"
          >
            <Truck size={13} /> <span className="hidden xl:inline">Track Order</span>
          </button>
          {customer && (
            <button
              data-testid="customer-signout-btn"
              onClick={signOut}
              title={`${customer.full_name || "Customer"} · ${customer.phone}`}
              className="hidden items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-slate-500 transition-colors hover:text-cyan-300 lg:flex"
            >
              <UserRound size={13} /> {customer.full_name?.split(" ")[0] || "Account"}
            </button>
          )}
          {showAdmin && (
            <Link
              to="/admin"
              data-testid="open-admin-modal-btn"
              className="hidden items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-slate-500 transition-colors hover:text-fuchsia-400 lg:flex"
            >
              <Lock size={13} /> Admin
            </Link>
          )}
          <button
            data-testid="open-order-drawer-btn"
            onClick={() => setDrawerOpen(true)}
            className="relative flex h-11 items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 px-4 text-sm font-semibold text-[#0A0B0E] transition-transform duration-300 hover:scale-105 active:scale-95 sm:px-5"
          >
            <ShoppingBag size={16} />
            Order
            {count > 0 && (
              <span
                data-testid="order-count-badge"
                className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-yellow-300 text-xs font-bold text-[#0A0B0E] shadow-lg"
              >
                {count}
              </span>
            )}
          </button>
          <div className="relative hidden lg:block" data-nav-menu>
            <button
              data-testid="nav-menu-bar-btn"
              onClick={() => setBarOpen((v) => !v)}
              className="flex h-11 items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-300 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
            >
              <Menu size={14} /> Menu
              <ChevronDown size={13} className={`transition-transform duration-300 ${barOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {barOpen && (
                <motion.nav
                  data-testid="nav-links"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18 }}
                  className="absolute right-0 top-[calc(100%+10px)] z-50 w-64 overflow-hidden rounded-2xl border border-white/10 bg-[#12141A] py-1.5 shadow-2xl shadow-black/60"
                >
                  {menuLinks.map((l) => (
                    <a
                      key={l.href + l.label}
                      href={l.href}
                      data-testid={testId(l.label)}
                      onClick={(e) => { setBarOpen(false); handleNavClick(e, l); }}
                      className="block px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-300 transition-colors duration-200 hover:bg-white/[0.05] hover:text-cyan-300"
                    >
                      {l.label}
                    </a>
                  ))}
                  {categories.length > 0 && (
                    <>
                      <button
                        data-testid="nav-categories-toggle"
                        onClick={() => setCatsOpen((v) => !v)}
                        className="flex w-full items-center justify-between px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-300 transition-colors duration-200 hover:bg-white/[0.05] hover:text-cyan-300"
                      >
                        <span className="flex items-center gap-2"><LayoutGrid size={13} /> Categories</span>
                        <ChevronDown size={13} className={`transition-transform duration-300 ${catsOpen ? "rotate-180" : ""}`} />
                      </button>
                      {catsOpen && (
                        <div data-testid="nav-categories-list" className="max-h-64 overflow-y-auto border-y border-white/5 bg-[#0A0B0E]/60 py-1">
                          {categories.map((c, i) => (
                            <button
                              key={c.id || c.name}
                              data-testid={`nav-category-${testId(c.name)}`}
                              onClick={() => openCategory(c.name)}
                              className="flex w-full items-center justify-between gap-3 px-6 py-2 text-left text-[12px] text-slate-300 transition-colors duration-200 hover:bg-cyan-400/10 hover:text-cyan-300"
                            >
                              <span className="flex min-w-0 items-center gap-2">
                                <span className="truncate">{c.name}</span>
                                {i === 0 && <span className="shrink-0 rounded-full bg-yellow-300/15 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.1em] text-yellow-300">Top</span>}
                              </span>
                              <span className="shrink-0 font-mono text-[10px] text-slate-600">{c.product_count}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  <div className="my-1.5 border-t border-white/10" />
                  <button
                    data-testid="download-catalogue-btn"
                    onClick={() => { setBarOpen(false); downloadCatalogue(); }}
                    disabled={downloading}
                    className="flex w-full items-center gap-2 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-cyan-300 transition-colors duration-200 hover:bg-cyan-400/10 disabled:opacity-40"
                  >
                    <Download size={14} /> {downloading ? "Preparing…" : "Download Catalogue"}
                  </button>
                </motion.nav>
              )}
            </AnimatePresence>
          </div>
          <button
            data-testid="nav-menu-btn"
            aria-label="Open menu"
            onClick={() => setMenuOpen(true)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors active:bg-white/10 lg:hidden"
          >
            <Menu size={18} />
          </button>
        </div>
      </div>

      {createPortal(
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#0A0B0E]/80 backdrop-blur-sm lg:hidden"
            onClick={() => setMenuOpen(false)}
            data-testid="nav-menu-overlay"
          >
            <motion.nav
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-testid="nav-mobile-menu"
              className="absolute right-0 top-0 flex h-full w-[82%] max-w-xs flex-col border-l border-white/10 bg-[#0F1015] px-6 py-6"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Menu</span>
                <button
                  data-testid="nav-menu-close-btn"
                  aria-label="Close menu"
                  onClick={() => setMenuOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-slate-400"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="mt-8 flex flex-col">
                {links.map((l) => (
                  <a
                    key={"m" + l.href + l.label}
                    href={l.href}
                    onClick={(e) => { setMenuOpen(false); handleNavClick(e, l); }}
                    data-testid={`mobile-${testId(l.label)}`}
                    className={`border-b border-white/5 py-4 font-display text-lg font-semibold transition-colors ${l.href === "#new-launch" ? "text-yellow-300" : "text-slate-200 active:text-cyan-300"}`}
                  >
                    {l.label}
                  </a>
                ))}
                {categories.length > 0 && (
                  <div className="border-b border-white/5">
                    <button
                      data-testid="mobile-nav-categories-toggle"
                      onClick={() => setMobileCatsOpen((v) => !v)}
                      className="flex w-full items-center justify-between py-4 font-display text-lg font-semibold text-slate-200 active:text-cyan-300"
                    >
                      <span className="flex items-center gap-2"><LayoutGrid size={17} /> Categories</span>
                      <ChevronDown size={16} className={`transition-transform duration-300 ${mobileCatsOpen ? "rotate-180" : ""}`} />
                    </button>
                    {mobileCatsOpen && (
                      <div data-testid="mobile-nav-categories-list" className="max-h-72 overflow-y-auto pb-3">
                        {categories.map((c) => (
                          <button
                            key={"m" + (c.id || c.name)}
                            data-testid={`mobile-nav-category-${testId(c.name)}`}
                            onClick={() => openCategory(c.name)}
                            className="flex w-full items-center justify-between gap-3 border-l border-white/10 py-2.5 pl-4 text-left text-sm text-slate-300 active:text-cyan-300"
                          >
                            <span className="truncate">{c.name}</span>
                            <span className="shrink-0 font-mono text-[10px] text-slate-600">{c.product_count}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-auto space-y-3 pt-6">
                <button
                  data-testid="mobile-download-catalogue-btn"
                  onClick={() => { downloadCatalogue(); setMenuOpen(false); }}
                  disabled={downloading}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-cyan-400/30 py-3 font-mono text-xs uppercase tracking-[0.15em] text-cyan-300 transition-colors active:bg-cyan-400/10 disabled:opacity-40"
                >
                  <Download size={15} /> {downloading ? "Preparing…" : "Download Catalogue"}
                </button>
                <button
                  data-testid="mobile-track-order-btn"
                  onClick={() => { setMenuOpen(false); window.dispatchEvent(new CustomEvent("vs:my-orders")); }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 py-3 font-mono text-xs uppercase tracking-[0.15em] text-slate-300 transition-colors active:bg-white/5"
                >
                  <Truck size={15} /> Track Your Order
                </button>
                {showAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setMenuOpen(false)}
                    data-testid="mobile-admin-link"
                    className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-fuchsia-300"
                  >
                    <Lock size={13} /> Admin dashboard
                  </Link>
                )}
                {customer && (
                  <button
                    data-testid="mobile-signout-btn"
                    onClick={() => { setMenuOpen(false); signOut(); }}
                    className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-slate-500"
                  >
                    <UserRound size={13} /> Sign out ({customer.full_name?.split(" ")[0] || customer.phone})
                  </button>
                )}
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>, document.body)}
    </header>
  );
}
